import type { Locale } from "@divespots/contracts";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../components/Button.tsx";
import { SegmentedControl } from "../components/SegmentedControl.tsx";
import { RATING_KEYS } from "./forms.ts";
import type { ClosedPeriodForm, FieldErrors, RatingKey } from "./forms.ts";
import { Field } from "./ui.tsx";

const RATING_VALUES = ["0", "1", "2", "3", "4", "5"] as const;

/** Six 0-5 controls, each with a "clear" action (null = not rated). */
export function RatingsEditor({
  value,
  onChange,
}: {
  value: Record<RatingKey, number | null>;
  onChange: (next: Record<RatingKey, number | null>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-3">
      {RATING_KEYS.map((k) => {
        const label = t(`ratings.${k}`);
        const current = value[k];
        return (
          <div key={k} className="adm-rating">
            <span className="ds-text-subheadline">{label}</span>
            <div className="adm-rating__controls">
              <SegmentedControl
                aria-label={label}
                // `""` selects nothing: an unrated aspect shows no pressed segment
                value={current === null ? "" : String(current)}
                onChange={(v) => onChange({ ...value, [k]: Number(v) })}
                options={RATING_VALUES.map((v) => ({ value: v as string, label: v }))}
              />
              <Button
                variant="plain"
                disabled={current === null}
                aria-label={`${label}: ${t("admin.ratings.clear")}`}
                onClick={() => onChange({ ...value, [k]: null })}
              >
                {t("admin.ratings.clear")}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Rows of recurring MM-DD ranges (they may wrap the new year) with an i18n reason for one locale. */
export function ClosedPeriodsEditor({
  value,
  onChange,
  locale,
  errors,
}: {
  value: ClosedPeriodForm[];
  onChange: (next: ClosedPeriodForm[]) => void;
  locale: Locale;
  errors: FieldErrors;
}) {
  const { t } = useTranslation();
  const set = (i: number, patch: Partial<ClosedPeriodForm>) =>
    onChange(value.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <div className="grid gap-3">
      {value.length === 0 ? (
        <p className="ds-text-subheadline text-label-secondary m-0">{t("admin.closed.none")}</p>
      ) : null}
      {value.map((p, i) => (
        <fieldset key={i} className="adm-period">
          <legend className="ds-text-footnote text-label-secondary">
            {t("admin.closed.period", { n: i + 1 })}
          </legend>
          <div className="adm-period__dates">
            <Field label={t("admin.closed.from")} error={errors[`closedPeriods.${i}.from`]}>
              {({ id, describedBy, invalid }) => (
                <input
                  id={id}
                  className="adm-input ds-text-coordinate"
                  inputMode="numeric"
                  placeholder="MM-DD"
                  maxLength={5}
                  value={p.from}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  onChange={(e) => set(i, { from: e.target.value })}
                />
              )}
            </Field>
            <Field label={t("admin.closed.to")} error={errors[`closedPeriods.${i}.to`]}>
              {({ id, describedBy, invalid }) => (
                <input
                  id={id}
                  className="adm-input ds-text-coordinate"
                  inputMode="numeric"
                  placeholder="MM-DD"
                  maxLength={5}
                  value={p.to}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  onChange={(e) => set(i, { to: e.target.value })}
                />
              )}
            </Field>
          </div>
          <Field
            label={`${t("admin.closed.reason")} (${locale.toUpperCase()})`}
            error={errors[`closedPeriods.${i}.reason`]}
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                lang={locale}
                className="adm-input"
                value={p.reason[locale]}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => set(i, { reason: { ...p.reason, [locale]: e.target.value } })}
              />
            )}
          </Field>
          <Button
            variant="destructive"
            icon={<Trash2 aria-hidden="true" strokeWidth={1.75} />}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            {t("admin.closed.remove", { n: i + 1 })}
          </Button>
        </fieldset>
      ))}
      <div>
        <Button
          variant="tinted"
          icon={<Plus aria-hidden="true" strokeWidth={1.75} />}
          disabled={value.length >= 12}
          onClick={() =>
            onChange([...value, { from: "", to: "", reason: { de: "", en: "", fr: "", it: "" } }])
          }
        >
          {t("admin.closed.add")}
        </Button>
      </div>
    </div>
  );
}
